/* eslint-disable */
/**
 * Webhooks settings page.
 *
 * Lets a brand admin manage outbound webhook subscriptions: register a
 * URL, choose events, view recent delivery attempts, rotate secret,
 * test, edit, delete.
 *
 * Two flows that involve the secret:
 *  - On create: API returns the secret ONCE in the response. We pop it
 *    in a one-time modal with a Copy button + a strong "you won't see
 *    this again" warning.
 *  - On rotate: same pattern — invalidates the old secret, returns the
 *    new one in the response, surfaced via the same one-time modal.
 *
 * URLs are truncated in the table so a 2000-char URL doesn't blow the
 * layout — full value is preserved server-side and shown in the edit
 * form.
 */
import { useEffect, useMemo, useState } from "react";
import api from "api";
import { toast } from "react-toastify";
import {
  Card,
  CardHeader,
  CardBody,
  Container,
  Row,
  Col,
  Table,
  Button,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
  Input,
  Spinner,
} from "reactstrap";

const SUPPORTED_EVENTS = [
  "scan.verified",
  "scan.repeated",
  "scan.invalid",
  "alert.repeat_scans",
  "scanner.created",
  "points.allocated",
  "redemption.created",
  "redemption.fulfilled",
];

// Pre-baked webhook templates the user can one-click add. Each template
// pre-fills the form modal — the user still has to paste their own URL
// before saving (no template ships with a real endpoint, since a copy-paste
// of a Slack/Discord webhook URL is the actual integration).
//
// Adding a new template: append a row here. `events` must be a subset of
// SUPPORTED_EVENTS or the API rejects the create.
const WEBHOOK_TEMPLATES = [
  {
    id: "counterfeit",
    title: "Counterfeit alerts",
    blurb:
      "Get pinged when a genuine code keeps getting re-scanned (5, 10, 25, 50 scans) or someone enters codes that aren't yours.",
    payload: {
      url: "https://hooks.slack.com/services/XXX/YYY/ZZZ",
      description: "Counterfeit alerts",
      events: ["alert.repeat_scans", "scan.invalid"],
      active: true,
    },
  },
  {
    id: "slack",
    title: "Slack channel",
    blurb:
      "Pipe redemption + signup events into a Slack channel. Paste your Incoming Webhook URL.",
    payload: {
      url: "https://hooks.slack.com/services/XXX/YYY/ZZZ",
      description: "Slack — orders + signups",
      events: ["redemption.created", "redemption.fulfilled", "scanner.created"],
      active: true,
    },
  },
  {
    id: "discord",
    title: "Discord channel",
    blurb:
      "Mirror reward activity into a Discord channel. Add `/slack` to your Discord webhook URL.",
    payload: {
      url: "https://discord.com/api/webhooks/XXX/YYY/slack",
      description: "Discord — reward activity",
      events: ["redemption.created", "redemption.fulfilled"],
      active: true,
    },
  },
  {
    id: "zapier",
    title: "Zapier (all events)",
    blurb:
      "Send every event to a Zapier catch-hook. Use Zap filters to branch by event type.",
    payload: {
      url: "https://hooks.zapier.com/hooks/catch/XXXXXX/YYYYYY/",
      description: "Zapier — all events",
      events: [...SUPPORTED_EVENTS],
      active: true,
    },
  },
  {
    id: "fulfillment",
    title: "Fulfillment-only feed",
    blurb:
      "Just redemption.created + redemption.fulfilled — clean stream for ops dashboards or warehouse tooling.",
    payload: {
      url: "https://your-server.example.com/veriscan/webhook",
      description: "Fulfillment events",
      events: ["redemption.created", "redemption.fulfilled"],
      active: true,
    },
  },
];

const truncate = (str, n = 60) => {
  if (!str) return "";
  return str.length > n ? str.slice(0, n) + "…" : str;
};

const formatTime = (d) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleString();
  } catch {
    return String(d);
  }
};

const StatusBadge = ({ status, code }) => {
  if (!status) return <span className="text-muted">never</span>;
  const color =
    status === "success" ? "success" : status === "failed" ? "danger" : "warning";
  return (
    <Badge color={color}>
      {status}
      {code ? ` ${code}` : ""}
    </Badge>
  );
};

// ---------- one-time secret reveal ----------
const SecretRevealModal = ({ secret, onClose }) => {
  const [copied, setCopied] = useState(false);
  if (!secret) return null;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(secret);
      setCopied(true);
      toast.success("Secret copied to clipboard.");
    } catch {
      toast.error("Could not copy. Select the value manually.");
    }
  };
  return (
    <Modal isOpen toggle={onClose} backdrop="static">
      <ModalHeader>Webhook secret</ModalHeader>
      <ModalBody>
        <p className="text-danger mb-2">
          <strong>Save this secret now — it will not be shown again.</strong>
        </p>
        <p className="text-muted small">
          Use it to verify the <code>X-VeriScan-Signature</code> header on each
          incoming webhook (HMAC-SHA256 over <code>{`${`}timestamp{`}`}.{`${`}body{`}`}</code>).
          If you lose it, rotate to generate a new one.
        </p>
        <Input type="textarea" rows={3} value={secret} readOnly />
      </ModalBody>
      <ModalFooter>
        <Button color="primary" onClick={copy}>
          {copied ? "Copied" : "Copy secret"}
        </Button>
        <Button color="secondary" onClick={onClose}>
          Done
        </Button>
      </ModalFooter>
    </Modal>
  );
};

// ---------- create / edit form ----------
const WebhookFormModal = ({ open, initial, onClose, onSaved }) => {
  const isEdit = !!initial?._id;
  const [url, setUrl] = useState("");
  const [description, setDescription] = useState("");
  const [events, setEvents] = useState([]);
  const [active, setActive] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setUrl(initial?.url || "");
      setDescription(initial?.description || "");
      setEvents(initial?.events || []);
      setActive(initial?.active !== false);
    }
  }, [open, initial]);

  const toggleEvent = (e) => {
    setEvents((cur) =>
      cur.includes(e) ? cur.filter((x) => x !== e) : [...cur, e]
    );
  };

  const submit = async () => {
    if (!url || !url.startsWith("https://")) {
      toast.error("URL must start with https://");
      return;
    }
    if (events.length === 0) {
      toast.error("Select at least one event.");
      return;
    }
    setSaving(true);
    try {
      const body = { url, description, events, active };
      const res = isEdit
        ? await api("put", `/webhooks/${initial._id}`, body)
        : await api("post", "/webhooks", body);
      if (res?.success) {
        toast.success(isEdit ? "Webhook saved." : "Webhook created.");
        onSaved(res);
      } else {
        toast.error(res?.message || "Save failed.");
      }
    } catch {
      // toast already shown by interceptor
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={open} toggle={onClose} size="lg">
      <ModalHeader toggle={onClose}>
        {isEdit ? "Edit webhook" : "Add webhook"}
      </ModalHeader>
      <ModalBody>
        <FormGroup>
          <Label>Endpoint URL</Label>
          <Input
            type="url"
            placeholder="https://example.com/webhooks/veriscan"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
          />
          <small className="text-muted">Must be HTTPS.</small>
        </FormGroup>
        <FormGroup>
          <Label>Description (optional)</Label>
          <Input
            type="text"
            maxLength={200}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="e.g. Zapier integration"
          />
        </FormGroup>
        <FormGroup>
          <Label>Events</Label>
          {SUPPORTED_EVENTS.map((e) => (
            <FormGroup check key={e}>
              <Label check>
                <Input
                  type="checkbox"
                  checked={events.includes(e)}
                  onChange={() => toggleEvent(e)}
                />{" "}
                <code>{e}</code>
              </Label>
            </FormGroup>
          ))}
        </FormGroup>
        {isEdit && (
          <FormGroup check>
            <Label check>
              <Input
                type="checkbox"
                checked={active}
                onChange={(e) => setActive(e.target.checked)}
              />{" "}
              Active
            </Label>
          </FormGroup>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button color="primary" onClick={submit} disabled={saving}>
          {saving ? <Spinner size="sm" /> : isEdit ? "Save" : "Create"}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

// ---------- deliveries drawer ----------
const DeliveriesModal = ({ open, webhookId, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  useEffect(() => {
    if (!open || !webhookId) return;
    let cancelled = false;
    setLoading(true);
    api("get", `/webhooks/${webhookId}/deliveries?limit=50`)
      .then((res) => {
        if (cancelled) return;
        if (res?.success) setRows(res.deliveries || []);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, webhookId]);
  return (
    <Modal isOpen={open} toggle={onClose} size="xl">
      <ModalHeader toggle={onClose}>Recent deliveries</ModalHeader>
      <ModalBody>
        {loading ? (
          <Spinner />
        ) : rows.length === 0 ? (
          <p className="text-muted">No deliveries yet.</p>
        ) : (
          <Table responsive size="sm">
            <thead>
              <tr>
                <th>When</th>
                <th>Event</th>
                <th>Attempt</th>
                <th>Status</th>
                <th>HTTP</th>
                <th>ms</th>
                <th>Error</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r._id}>
                  <td>{formatTime(r.createdAt)}</td>
                  <td>
                    <code>{r.event}</code>
                  </td>
                  <td>{r.attempt}</td>
                  <td>
                    <StatusBadge status={r.status} />
                  </td>
                  <td>{r.responseStatus || "—"}</td>
                  <td>{r.durationMs || "—"}</td>
                  <td className="text-danger">
                    {truncate(r.error || "", 80)}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={onClose}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};

// ---------- main page ----------
const Webhooks = () => {
  const [loading, setLoading] = useState(true);
  const [webhooks, setWebhooks] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [revealedSecret, setRevealedSecret] = useState("");
  const [deliveriesFor, setDeliveriesFor] = useState(null);
  const [busyId, setBusyId] = useState("");

  const refresh = async () => {
    setLoading(true);
    try {
      const res = await api("get", "/webhooks");
      if (res?.success) setWebhooks(res.webhooks || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const onSaved = (res) => {
    setFormOpen(false);
    setEditTarget(null);
    refresh();
    if (res?.secret) setRevealedSecret(res.secret);
  };

  const handleTest = async (id) => {
    setBusyId(id);
    try {
      const res = await api("post", `/webhooks/${id}/test`);
      if (res?.success) {
        const r = res.result || {};
        if (r.success) {
          toast.success(`Test fired: HTTP ${r.status}`);
        } else {
          toast.error(`Test failed: ${r.error || "unknown error"}`);
        }
      }
    } finally {
      setBusyId("");
    }
  };

  const handleRotate = async (id) => {
    if (!window.confirm("Rotate the secret? The current secret will stop working immediately.")) return;
    setBusyId(id);
    try {
      const res = await api("post", `/webhooks/${id}/rotate-secret`);
      if (res?.success) {
        toast.success("Secret rotated.");
        if (res.secret) setRevealedSecret(res.secret);
        refresh();
      }
    } finally {
      setBusyId("");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this webhook? This cannot be undone.")) return;
    setBusyId(id);
    try {
      const res = await api("delete", `/webhooks/${id}`);
      if (res?.success) {
        toast.success("Webhook deleted.");
        refresh();
      }
    } finally {
      setBusyId("");
    }
  };

  return (
    <>
      {/* No `mt--7` here — that's an Argon dashboard pattern for pulling a
          card up over a colored banner header. Inside the Settings hub
          there's no banner, so the negative margin overlapped this card
          on top of the section description. Plain Container only. */}
      <Container fluid className="px-0">
        <Row className="m-0">
          <Col className="p-0">
            <Card className="shadow" style={{ borderRadius: 12, border: "none" }}>
              <CardHeader className="border-0 d-flex justify-content-between align-items-center">
                <h3 className="mb-0">Webhooks</h3>
                <Button
                  color="primary"
                  onClick={() => {
                    setEditTarget(null);
                    setFormOpen(true);
                  }}
                >
                  Add webhook
                </Button>
              </CardHeader>
              <CardBody>
                {/* Always-visible templates strip — quick way to scaffold a
                    common integration without building the form from scratch.
                    Same component is reused in the empty state below; here
                    it sits above the existing-webhooks table so admins can
                    add another integration in two clicks. */}
                {!loading && (
                  <div style={{ marginBottom: 18 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>
                      Templates
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 10 }}>
                      {WEBHOOK_TEMPLATES.map((t) => (
                        <button
                          key={t.id}
                          type="button"
                          onClick={() => {
                            setEditTarget(t.payload);
                            setFormOpen(true);
                          }}
                          style={{
                            textAlign: "left",
                            background: "#fff",
                            border: "1px solid #E5E7EB",
                            borderRadius: 10,
                            padding: 12,
                            cursor: "pointer",
                            transition: "border-color 120ms, box-shadow 120ms",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.borderColor = "#362FD8";
                            e.currentTarget.style.boxShadow = "0 1px 4px rgba(54,47,216,0.12)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.borderColor = "#E5E7EB";
                            e.currentTarget.style.boxShadow = "none";
                          }}
                        >
                          <div style={{ fontWeight: 600, color: "#111827", marginBottom: 4, fontSize: 14 }}>
                            {t.title}
                          </div>
                          <div style={{ fontSize: 12, color: "#6B7280", lineHeight: 1.4 }}>
                            {t.blurb}
                          </div>
                          <div style={{ fontSize: 11, color: "#362FD8", marginTop: 8, fontWeight: 600 }}>
                            Use template →
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {loading ? (
                  <Spinner />
                ) : webhooks.length === 0 ? (
                  <p className="text-muted" style={{ marginTop: 12 }}>
                    No webhooks yet. Pick a template above or click{" "}
                    <strong>Add webhook</strong> for a blank one.
                  </p>
                ) : (
                  <Table responsive>
                    <thead>
                      <tr>
                        <th>URL</th>
                        <th>Events</th>
                        <th>Status</th>
                        <th>Last delivery</th>
                        <th style={{ width: 280 }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {webhooks.map((w) => (
                        <tr key={w._id}>
                          <td title={w.url}>
                            <code>{truncate(w.url, 50)}</code>
                            {w.description && (
                              <div className="text-muted small">
                                {w.description}
                              </div>
                            )}
                          </td>
                          <td>
                            {(w.events || []).map((e) => (
                              <Badge
                                key={e}
                                color="info"
                                className="mr-1 mb-1"
                              >
                                {e}
                              </Badge>
                            ))}
                          </td>
                          <td>
                            {w.active ? (
                              <Badge color="success">Active</Badge>
                            ) : (
                              <Badge color="secondary">Disabled</Badge>
                            )}
                          </td>
                          <td>
                            <StatusBadge
                              status={w.lastDelivery?.lastStatus}
                              code={w.lastDelivery?.lastResponseStatus}
                            />
                            <div className="text-muted small">
                              {formatTime(w.lastDelivery?.lastDeliveredAt)}
                            </div>
                          </td>
                          <td>
                            <Button
                              size="sm"
                              color="secondary"
                              onClick={() => {
                                setEditTarget(w);
                                setFormOpen(true);
                              }}
                              disabled={busyId === w._id}
                            >
                              Edit
                            </Button>{" "}
                            <Button
                              size="sm"
                              color="info"
                              onClick={() => handleTest(w._id)}
                              disabled={busyId === w._id}
                            >
                              Test
                            </Button>{" "}
                            <Button
                              size="sm"
                              color="default"
                              onClick={() => setDeliveriesFor(w._id)}
                              disabled={busyId === w._id}
                            >
                              Deliveries
                            </Button>{" "}
                            <Button
                              size="sm"
                              color="warning"
                              onClick={() => handleRotate(w._id)}
                              disabled={busyId === w._id}
                            >
                              Rotate
                            </Button>{" "}
                            <Button
                              size="sm"
                              color="danger"
                              onClick={() => handleDelete(w._id)}
                              disabled={busyId === w._id}
                            >
                              Delete
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <WebhookFormModal
        open={formOpen}
        initial={editTarget}
        onClose={() => {
          setFormOpen(false);
          setEditTarget(null);
        }}
        onSaved={onSaved}
      />
      <SecretRevealModal
        secret={revealedSecret}
        onClose={() => setRevealedSecret("")}
      />
      <DeliveriesModal
        open={!!deliveriesFor}
        webhookId={deliveriesFor}
        onClose={() => setDeliveriesFor(null)}
      />
    </>
  );
};

export default Webhooks;
